import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createScheduleSchema,
  createTriggerSchema,
  repositoryEventTypes,
} from "@zworks/console-contracts";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ApiError, flowsQuery, flowVersionsQuery } from "@/lib/api";
import { createSchedule, createTrigger } from "@/lib/automation";
import { automationTemplates } from "@/pages/automation-templates";
const selectClass =
  "h-9 w-full rounded-md border border-input bg-card px-3 text-sm focus-visible:outline-2 focus-visible:outline-ring disabled:bg-muted disabled:text-muted-foreground";
/** The repository events the platform admits, in the wording the reader expects. */
const repositoryEventLabels: Record<string, string> = {
  "pull_request.opened": "Pull request opened",
  "pull_request.updated": "Pull request updated",
  "pull_request.merged": "Pull request merged",
  push: "Push",
  "check_run.failed": "Check failed",
  "issues.labeled": "Issue labelled",
};

export function AutomationForm({
  projectId,
  kind,
  onCreated,
  onDenied,
}: {
  projectId: string;
  kind: "schedule" | "trigger";
  onCreated: () => void;
  onDenied: () => void;
}) {
  const [name, setName] = useState("");
  const [cron, setCron] = useState("0 9 * * 1");
  const [flowId, setFlowId] = useState("");
  const [versionId, setVersionId] = useState("");
  const [intent, setIntent] = useState("");
  const [branch, setBranch] = useState("main");
  const [cap, setCap] = useState("");
  const [source, setSource] = useState<"webhook" | "repository-event">("webhook");
  const [event, setEvent] = useState("");
  const [repositoryRef, setRepositoryRef] = useState("");
  const [secret, setSecret] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [templateSummary, setTemplateSummary] = useState("");

  function applyTemplate(id: string) {
    const template = automationTemplates.find((entry) => entry.id === id);
    if (!template) {
      setTemplateId("");
      setTemplateSummary("");
      return;
    }
    setTemplateId(id);
    setTemplateSummary(template.summary);
    setName(template.title);
    setIntent(template.intent);
    if (template.kind === "schedule") setCron(template.cron);
    else setEvent(template.event);
  }
  const flows = useQuery(flowsQuery(projectId));
  const versions = useQuery({ ...flowVersionsQuery(projectId, flowId), enabled: !!flowId });
  const selected = versions.data?.find((v) => v.id === versionId);
  const base = {
    flowId,
    flowVersionId: versionId,
    flowVersion: selected?.number ?? 0,
    run: { intent, branch, ...(cap ? { creditCap: Number(cap) } : {}) },
  };
  const schedule = createScheduleSchema.safeParse({ ...base, name, cron });
  const trigger = createTriggerSchema.safeParse({
    ...base,
    source,
    event,
    repositoryRef: source === "repository-event" ? repositoryRef : null,
    signingSecret: secret,
  });
  const valid =
    kind === "schedule"
      ? schedule.success
      : trigger.success &&
        secret.length >= 16 &&
        (source !== "repository-event" || !!repositoryRef);
  const create = useMutation({
    mutationFn: async () => {
      if (kind === "schedule" && schedule.success) return createSchedule(projectId, schedule.data);
      if (kind === "trigger" && trigger.success) return createTrigger(projectId, trigger.data);
      throw new Error("Complete the required fields.");
    },
    onSuccess: () => {
      setSecret("");
      onCreated();
    },
    onError: (error) => {
      if (error instanceof ApiError && [403, 404].includes(error.status)) onDenied();
    },
  });
  return (
    <form
      className="grid gap-3 rounded-lg border bg-card p-5 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) create.mutate();
      }}
    >
      <h2 className="text-base font-semibold sm:col-span-2">New {kind}</h2>
      {/* A starting point, not a rule: choosing one fills the fields below from a template the
          create endpoint will accept, and everything stays editable afterwards. */}
      <label className="grid gap-1 text-sm sm:col-span-2">
        Start from a template
        <select
          className={selectClass}
          value={templateId}
          onChange={(e) => applyTemplate(e.target.value)}
        >
          <option value="">Start from scratch</option>
          {automationTemplates
            .filter((template) => template.kind === kind)
            .map((template) => (
              <option key={template.id} value={template.id}>
                {template.title}
              </option>
            ))}
        </select>
      </label>
      {templateSummary && (
        <p className="text-sm text-muted-foreground sm:col-span-2" role="note">
          {templateSummary}
        </p>
      )}
      {kind === "schedule" ? (
        <>
          <label className="grid gap-1 text-sm">
            Schedule name
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
          </label>
          <label className="grid gap-1 text-sm">
            Cron expression
            <Input value={cron} onChange={(e) => setCron(e.target.value)} maxLength={120} />
            <span className="text-xs text-muted-foreground">
              Five fields, interpreted in the project time zone.
            </span>
          </label>
        </>
      ) : (
        <>
          <label className="grid gap-1 text-sm">
            Source
            <select
              className={selectClass}
              value={source}
              onChange={(e) => {
                setSource(e.target.value as typeof source);
                setEvent("");
              }}
            >
              <option value="webhook">Webhook</option>
              <option value="repository-event">Repository event</option>
            </select>
          </label>
          {source === "repository-event" ? (
            <label className="grid gap-1 text-sm">
              Repository event
              <select
                className={selectClass}
                value={event}
                onChange={(e) => setEvent(e.target.value)}
              >
                <option value="">Choose an event</option>
                {repositoryEventTypes.map((name) => (
                  <option key={name} value={name}>
                    {repositoryEventLabels[name] ?? name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label className="grid gap-1 text-sm">
              Event
              <Input value={event} onChange={(e) => setEvent(e.target.value)} maxLength={120} />
            </label>
          )}
          {source === "repository-event" && (
            <label className="grid gap-1 text-sm">
              Bound repository
              <Input
                value={repositoryRef}
                onChange={(e) => setRepositoryRef(e.target.value)}
                maxLength={200}
                placeholder="owner/repository"
              />
            </label>
          )}
          <label className="grid gap-1 text-sm">
            Signing secret
            <Input
              type="password"
              autoComplete="new-password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              minLength={16}
              maxLength={400}
            />
            <span className="text-xs text-muted-foreground">
              At least 16 characters. Kept privately and never returned.
            </span>
          </label>
        </>
      )}
      <label className="grid gap-1 text-sm">
        Flow
        <select
          className={selectClass}
          value={flowId}
          onChange={(e) => {
            setFlowId(e.target.value);
            setVersionId("");
          }}
        >
          <option value="">Choose an active flow</option>
          {flows.data
            ?.filter((f) => f.state === "active")
            .map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
        </select>
      </label>
      <label className="grid gap-1 text-sm">
        Pinned version
        <select
          className={selectClass}
          value={versionId}
          disabled={!flowId || versions.isPending}
          onChange={(e) => setVersionId(e.target.value)}
        >
          <option value="">Choose a ready version</option>
          {versions.data
            ?.filter((v) => v.readiness === "ready")
            .map((v) => (
              <option key={v.id} value={v.id}>
                Version {v.number}
              </option>
            ))}
        </select>
      </label>
      <label className="grid gap-1 text-sm sm:col-span-2">
        Intent
        <Input value={intent} onChange={(e) => setIntent(e.target.value)} maxLength={2000} />
      </label>
      <label className="grid gap-1 text-sm">
        Branch
        <Input value={branch} onChange={(e) => setBranch(e.target.value)} maxLength={120} />
      </label>
      <label className="grid gap-1 text-sm">
        Credit cap (optional)
        <Input type="number" min={1} value={cap} onChange={(e) => setCap(e.target.value)} />
      </label>
      <p className="text-xs text-muted-foreground sm:col-span-2">
        Omitted limits, model tier, and effort use trusted project settings when the rule fires.
        Payload text never changes the run intent.
      </p>
      {(flows.isError || versions.isError) && (
        <p role="alert" className="text-sm text-destructive sm:col-span-2">
          Flows could not be loaded. Close and reopen the form to try again.
        </p>
      )}
      {create.isError && (
        <p role="alert" className="text-sm text-destructive sm:col-span-2">
          {create.error instanceof ApiError
            ? (create.error.problem?.detail ?? "The rule could not be created.")
            : "Check the required fields."}
        </p>
      )}
      <div>
        <Button type="submit" disabled={!valid || create.isPending}>
          {create.isPending ? "Creating…" : `Create ${kind}`}
        </Button>
      </div>
    </form>
  );
}
